/** Build UserLocationMapping rows from multi-select dropdown values. */
const DEFAULT_CODE = "0000";

const norm = (codes) =>
  (Array.isArray(codes) ? codes : [])
    .map((c) => String(c ?? "").trim())
    .filter((c) => c && c !== DEFAULT_CODE && c.toLowerCase() !== "all");

const findTerritory = (list, code) =>
  (list || []).find(
    (t) => String(t.territoryCode || t.TerritoryCode || "").trim() === code
  );

const findRegion = (list, code) =>
  (list || []).find(
    (r) => String(r.regionCode || r.RegionCode || "").trim() === code
  );

const findZone = (list, code) => {
  const sel = String(code ?? "").trim();
  return (list || []).find((z) => {
    const zc = String(z.zoneCode || z.ZoneCode || "").trim();
    if (zc === sel) return true;
    return (
      Array.isArray(z.zoneCodes) &&
      z.zoneCodes.some((c) => String(c ?? "").trim() === sel)
    );
  });
};

export function buildLocationMappingsFromSelections(
  { countryCodes, zoneCodes, regionCodes, territoryCodes },
  {
    territories = [],
    regions = [],
    zones = [],
    countryScopeMode = "",
    zoneScopeMode = "",
    regionScopeMode = "",
    territoryScopeMode = "",
    existingMappings = [],
  } = {}
) {
  const savedFor = (code, keys) =>
    (existingMappings || []).find((m) =>
      keys.some((k) => String(m?.[k] ?? "").trim() === code)
    );
  const countries = norm(countryCodes);
  const zonesSel = norm(zoneCodes);
  const regionsSel = norm(regionCodes);
  const territoriesSel = norm(territoryCodes);
  const rows = [];
  const seen = new Set();

  const add = (countryCode, zoneCode, regionCode, territoryCode) => {
    const cc = countryCode || DEFAULT_CODE;
    const zc = zoneCode || DEFAULT_CODE;
    const rc = regionCode || DEFAULT_CODE;
    const tc = territoryCode || DEFAULT_CODE;
    const key = `${cc}|${zc}|${rc}|${tc}`;
    if (seen.has(key)) return;
    if (cc === DEFAULT_CODE && zc === DEFAULT_CODE && rc === DEFAULT_CODE && tc === DEFAULT_CODE)
      return;
    seen.add(key);
    rows.push({ countryCode: cc, zoneCode: zc, regionCode: rc, territoryCode: tc });
  };

  const mode = (v) => String(v || "").trim().toUpperCase();
  // Whole-country (or whole selected countries): UI "All" on Zone/Region/Territory
  // must save as Country|0000|0000|0000 — same coverage, no conflicting child rows.
  if (
    countries.length &&
    (mode(zoneScopeMode) === "ALL" || !zonesSel.length) &&
    (mode(regionScopeMode) === "ALL" || !regionsSel.length) &&
    (mode(territoryScopeMode) === "ALL" || !territoriesSel.length) &&
    (mode(zoneScopeMode) === "ALL" ||
      mode(regionScopeMode) === "ALL" ||
      mode(territoryScopeMode) === "ALL" ||
      (!zonesSel.length && !regionsSel.length && !territoriesSel.length))
  ) {
    // Country only, or Country + All below → one row per country.
    if (
      (!zonesSel.length && !regionsSel.length && !territoriesSel.length) ||
      (mode(zoneScopeMode) === "ALL" &&
        mode(regionScopeMode) === "ALL" &&
        mode(territoryScopeMode) === "ALL")
    ) {
      countries.forEach((cc) => add(cc, DEFAULT_CODE, DEFAULT_CODE, DEFAULT_CODE));
      return rows;
    }
  }

  if (territoriesSel.length) {
    territoriesSel.forEach((tc) => {
      const t = findTerritory(territories, tc);
      if (t) {
        add(
          t.countryCode || t.CountryCode,
          t.zoneCode || t.ZoneCode,
          t.regionCode || t.RegionCode,
          t.territoryCode || t.TerritoryCode
        );
      } else {
        const saved = savedFor(tc, ["territoryCode", "TerritoryCode", "TERRITORYCODE"]);
        add(
          saved?.countryCode || saved?.CountryCode || countries[0],
          saved?.zoneCode || saved?.ZoneCode || zonesSel[0],
          saved?.regionCode || saved?.RegionCode || regionsSel[0],
          tc
        );
      }
    });
    return rows;
  }

  if (regionsSel.length) {
    regionsSel.forEach((rc) => {
      const r = findRegion(regions, rc);
      if (r) {
        add(r.countryCode || r.CountryCode, r.zoneCode || r.ZoneCode, r.regionCode || r.RegionCode, DEFAULT_CODE);
      } else {
        const saved = savedFor(rc, ["regionCode", "RegionCode"]);
        add(
          saved?.countryCode || saved?.CountryCode || countries[0],
          saved?.zoneCode || saved?.ZoneCode || zonesSel[0],
          rc,
          DEFAULT_CODE
        );
      }
    });
    return rows;
  }

  if (zonesSel.length) {
    zonesSel.forEach((zc) => {
      const z = findZone(zones, zc);
      if (z) {
        const canonicalZone = z.zoneCode || z.ZoneCode || zc;
        const linkedCountries = Array.isArray(z.countryCodes) && z.countryCodes.length
          ? z.countryCodes.map((c) => String(c).trim()).filter(Boolean)
          : [String(z.countryCode || z.CountryCode || "").trim()].filter(Boolean);
        if (countries.length) {
          countries.forEach((cc) => {
            if (linkedCountries.length && !linkedCountries.includes(cc)) return;
            const zoneForCc =
              (z.zoneCodeByCountry && z.zoneCodeByCountry[cc]) || canonicalZone;
            add(cc, zoneForCc, DEFAULT_CODE, DEFAULT_CODE);
          });
        } else {
          add(
            z.countryCode || z.CountryCode || DEFAULT_CODE,
            canonicalZone,
            DEFAULT_CODE,
            DEFAULT_CODE
          );
        }
      } else {
        const saved = savedFor(zc, ["zoneCode", "ZoneCode"]);
        add(saved?.countryCode || saved?.CountryCode || countries[0], zc, DEFAULT_CODE, DEFAULT_CODE);
      }
    });
    return rows;
  }

  countries.forEach((cc) => add(cc, DEFAULT_CODE, DEFAULT_CODE, DEFAULT_CODE));
  return rows;
}

/** Derive dropdown selections from API locationMappings or legacy CSV arrays. */
export function applyLocationMappingsToForm(userRow) {
  const raw =
    userRow?.locationMappings ||
    userRow?.LocationMappings ||
    [];

  if (!Array.isArray(raw) || !raw.length) return null;

  const countryCodes = [];
  const zoneCodes = [];
  const regionCodes = [];
  const territoryCodes = [];
  const pushUnique = (arr, val) => {
    const v = String(val ?? "").trim();
    if (!v || v === DEFAULT_CODE) return;
    if (!arr.includes(v)) arr.push(v);
  };

  raw.forEach((m) => {
    pushUnique(countryCodes, m.countryCode ?? m.CountryCode);
    pushUnique(zoneCodes, m.zoneCode ?? m.ZoneCode);
    pushUnique(regionCodes, m.regionCode ?? m.RegionCode);
    pushUnique(territoryCodes, m.territoryCode ?? m.TerritoryCode);
  });

  return { countryCodes, zoneCodes, regionCodes, territoryCodes, locationMappings: raw };
}
